import { vec3 } from 'gl-matrix';
import type { RoiId } from '~/domain/changes/ids';
import { gl } from '~/engine/Engine';
import { RenderStorage } from '~/engine/core/render/RenderStorage';
import { Grid } from '~/engine/data/common/Grid';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { SDFOperationRenderer } from '~/engine/util/algorithms/sdf/BooleanRenderer';
import { BooleanOperation } from '~/engine/util/algorithms/sdf/SDFOperations';
import type { DrawingConstraints } from '~/engine/tool/actions/DrawActionState';

/**
 * Constraint structure SDFs that should actually be applied to a stroke,
 * resolved from the configured constraints against the target ROI.
 */
export interface ResolvedConstraintTextures {
    /** Bounding structure to intersect with (stay inside), or null. */
    boundingTexture: ContourTexture | null;
    /** Protected structures to subtract (stay outside). */
    protectedTextures: ContourTexture[];
}

/**
 * Decide which constraint structure SDFs apply to a stroke. This is the single
 * source of truth for the constraint-enforcement exclusions, shared by the
 * live preview and the commit clip:
 *
 *  - The target structure is excluded from both sets — clipping a structure to
 *    itself, or subtracting it from its own stroke, would erase the stroke.
 *  - The active bounding structure is excluded from the protected set — the
 *    clip intersects the stroke with the bound and then subtracts the protected
 *    union, so a structure that is BOTH the Inside bound and Outside-protected
 *    would AND then AND_NOT the same SDF and erase the entire stroke. This is
 *    the safety invariant's single point of enforcement: it holds here even if
 *    a caller (or a future UI without the pruning effect) hands us overlapping
 *    sets.
 *  - Structures without an SDF texture (e.g. point ROIs) are skipped.
 *
 * Accessing `.texture` lazily builds the structure's SDF if needed, so a freshly
 * resolved ROI is usable immediately rather than silently contributing nothing.
 */
export function resolveConstraintTextures(
    bounding: RegionOfInterest | undefined,
    protectedStructures: readonly RegionOfInterest[] | undefined,
    targetRoiId: RoiId
): ResolvedConstraintTextures {
    // The bounding structure is only actually applied (AND-ed) when it is not
    // the target itself; only then must it be kept out of the protected set.
    const boundingApplies = !!bounding && bounding.roiId !== targetRoiId;
    const boundingTexture = boundingApplies ? (bounding.texture ?? null) : null;
    const excludedBoundingRoiId = boundingApplies ? bounding.roiId : null;

    const protectedTextures = (protectedStructures ?? [])
        .filter(
            (roi) =>
                roi.roiId !== targetRoiId && roi.roiId !== excludedBoundingRoiId
        )
        .map((roi) => roi.texture)
        .filter((tex): tex is ContourTexture => !!tex);

    return { boundingTexture, protectedTextures };
}

/**
 * Rasterize the given structure SDFs onto a fresh texture aligned to
 * `gridSource`'s grid — i.e. their union, zero everywhere the sources are
 * absent. Returns null for an empty source list. The caller owns the returned
 * texture and must {@link ContourTexture.delete} it.
 *
 * Building the mask on the *stroke's* grid (rather than booling against the
 * structure textures directly) keeps the work bounded to the stroke even when
 * a source structure (e.g. BODY/External) spans the whole image, and gives a
 * mask that fully covers the stroke so the subsequent AND is a true
 * intersection everywhere (not only where the source's own grid reaches).
 */
export function buildConstraintMask(
    gridSource: ContourTexture,
    sources: ContourTexture[]
): ContourTexture | null {
    if (sources.length === 0) {
        return null;
    }
    const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
    // Empty texture aligned to gridSource's grid.
    const mask = gridSource.unionAligned(gridSource);
    for (const source of sources) {
        renderer.renderOnDestination(mask, BooleanOperation.OR, source);
    }
    return mask;
}

/**
 * Constraint masks rasterized onto one stroke grid: the bounding structure
 * to intersect with (stay inside), the in-range intensity voxels to
 * intersect with, and the union of protected structures to subtract (stay
 * outside). A null member does not apply. The owner deletes them with
 * {@link deleteConstraintMasks}.
 */
export interface ConstraintMasks {
    bounding: ContourTexture | null;
    intensity: ContourTexture | null;
    protected: ContourTexture | null;
}

/**
 * Rasterize every {@link DrawingConstraints} term that applies to a stroke
 * onto `stroke`'s grid (see {@link resolveConstraintTextures},
 * {@link buildConstraintMask} and {@link buildIntensityMask}).
 *
 * @param stroke Texture whose grid the masks are built on
 * @param constraints The stroke's constraints; undefined applies none
 * @param image Image the intensity threshold samples
 * @param targetRoiId The structure the stroke is committed onto
 * @returns The masks, or null when no constraint applies
 */
export function buildConstraintMasks(
    stroke: ContourTexture,
    constraints: DrawingConstraints | undefined,
    image: ImageObject,
    targetRoiId: RoiId
): ConstraintMasks | null {
    const { boundingTexture, protectedTextures } = resolveConstraintTextures(
        constraints?.bounding,
        constraints?.protectedStructures,
        targetRoiId
    );
    const range = constraints?.intensityThreshold;
    if (!boundingTexture && protectedTextures.length === 0 && !range) {
        return null;
    }
    const masks: ConstraintMasks = {
        bounding: null,
        intensity: null,
        protected: null,
    };
    try {
        if (boundingTexture) {
            masks.bounding = buildConstraintMask(stroke, [boundingTexture]);
        }
        if (range) {
            masks.intensity = buildIntensityMask(
                stroke,
                image,
                range.rawLower,
                range.rawUpper
            );
        }
        masks.protected = buildConstraintMask(stroke, protectedTextures);
    } catch (error) {
        deleteConstraintMasks(masks);
        throw error;
    }
    return masks;
}

/** Delete every texture {@link buildConstraintMasks} built. */
export function deleteConstraintMasks(masks: ConstraintMasks): void {
    masks.bounding?.delete();
    masks.intensity?.delete();
    masks.protected?.delete();
}

/**
 * Apply prebuilt constraint masks to `target` in place:
 *  - intersect with the bounding mask (stay inside), then
 *  - intersect with the intensity mask (stay within the threshold band), then
 *  - subtract the protected-union mask (stay outside).
 *
 * All masks must be aligned to / cover `target`'s grid (see
 * {@link buildConstraintMasks}). The boolean renderer leaves the blend
 * equation at MIN/MAX, so this restores the additive default afterwards —
 * otherwise a following `renderLayers` would composite with MIN and blank
 * the canvas.
 */
export function applyConstraintMasks(
    target: ContourTexture,
    masks: ConstraintMasks
): void {
    const {
        bounding: boundingMask,
        intensity: intensityMask,
        protected: protectedMask,
    } = masks;
    const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
    if (boundingMask) {
        renderer.renderOnDestination(
            target,
            BooleanOperation.AND,
            boundingMask
        );
    }
    if (intensityMask) {
        renderer.renderOnDestination(
            target,
            BooleanOperation.AND,
            intensityMask
        );
    }
    if (protectedMask) {
        renderer.renderOnDestination(
            target,
            BooleanOperation.AND_NOT,
            protectedMask
        );
    }
    gl.blendEquation(gl.FUNC_ADD);
}

// ── Intensity threshold ───────────────────────────────────────────────

/**
 * Classify one Z-slice of `maskGrid` into a binary in-range SDF byte slice by
 * sampling the image volume at each mask voxel's **world-space center**
 * (nearest-neighbour) and testing `rawLower <= intensity <= rawUpper`: 255
 * (inside) when in range, else 0 (outside). A subsequent intersection (AND /
 * min) then keeps a stroke only on in-range voxels.
 *
 * Because it samples by world position, it works for ANY orientation / spacing /
 * sub-voxel offset between the mask grid and the image grid — transversal, sagittal,
 * coronal, and anisotropic views all classify correctly. Mask voxels whose
 * center falls outside the image volume, or that sample a non-finite value, are
 * classified out of range (0), so contour is never deposited where the intensity
 * is unknown. Pure (Grid math only, no GL) so it is unit-testable in isolation.
 *
 * When `maskGrid` and `imageGrid` are the same Grid instance (the
 * body-contour extraction thresholds the image over its own grid) the
 * world-space round trip is skipped for a direct linear scan with identical
 * classification — see the fast path inside.
 *
 * @param maskGrid Grid of the mask being built (the stroke's texture grid).
 * @param imageGrid Un-transformed image grid the `floatPixelData` is indexed by.
 * @param floatPixelData Row-major raw intensity volume aligned with `imageGrid`.
 * @param z Mask-grid Z index of the slice to classify.
 * @param rawLower Inclusive lower bound in the image's raw voxel scale.
 * @param rawUpper Inclusive upper bound in the image's raw voxel scale.
 */
export function sampleIntensityMaskSlice(
    maskGrid: Grid,
    imageGrid: Grid,
    floatPixelData: ArrayLike<number>,
    z: number,
    rawLower: number,
    rawUpper: number
): Uint8Array {
    const sx = maskGrid.sizeX;
    const sy = maskGrid.sizeY;
    const slice = new Uint8Array(sx * sy);
    const ix = imageGrid.sizeX;
    const iy = imageGrid.sizeY;
    const iz = imageGrid.sizeZ;
    const sliceStride = ix * iy;

    // Identity fast path: when the mask grid IS the image grid the
    // world-space round trip below computes the identity index mapping —
    // at two mat4 transforms and several vec3 allocations per voxel, that
    // was ~60% of the whole body-contour CPU pipeline (measured 69× slower
    // than this linear scan on a 512×512×200 volume). The classification
    // is bit-identical: same inclusive bounds, and NaN comparisons are
    // false so non-finite samples stay out of range.
    if (maskGrid === imageGrid) {
        const base = z * sliceStride;
        for (let i = 0; i < sliceStride; i++) {
            const v = floatPixelData[base + i];
            if (v >= rawLower && v <= rawUpper) {
                slice[i] = 255;
            }
        }
        return slice;
    }

    // Scratch vectors hoisted out of the loop and transformed in place:
    // Grid's convenience transforms allocate fresh vec3s per call, which
    // at volume scale is hundreds of millions of short-lived allocations.
    const voxel = vec3.create();
    const world = vec3.create();
    const iv = vec3.create();
    const maskVoxelToWorld = maskGrid.voxelToWorld;
    const imageWorldToVoxel = imageGrid.worldToVoxel;
    for (let y = 0; y < sy; y++) {
        const rowBase = y * sx;
        for (let x = 0; x < sx; x++) {
            // voxelCenterToWorldSpace inlined: center offset, then the
            // same two matrices it would apply.
            vec3.set(voxel, x + 0.5, y + 0.5, z + 0.5);
            vec3.transformMat4(world, voxel, maskVoxelToWorld);
            vec3.transformMat4(iv, world, imageWorldToVoxel);
            const vx = Math.floor(iv[0]);
            const vy = Math.floor(iv[1]);
            const vz = Math.floor(iv[2]);
            if (
                vx < 0 ||
                vx >= ix ||
                vy < 0 ||
                vy >= iy ||
                vz < 0 ||
                vz >= iz
            ) {
                continue; // outside the image volume → out of range
            }
            const v = floatPixelData[vz * sliceStride + vy * ix + vx];
            // NaN comparisons are false, so non-finite samples stay out of range.
            if (v >= rawLower && v <= rawUpper) {
                slice[rowBase + x] = 255;
            }
        }
    }
    return slice;
}

/**
 * Build an intensity-threshold mask covering `stroke`'s grid: a binary SDF that
 * is "inside" exactly on the voxels whose underlying image intensity is in
 * `[rawLower, rawUpper]`. ANDing this into a stroke (see
 * {@link applyConstraintMasks}) keeps drawing only on in-range voxels. The
 * caller owns the returned texture and must {@link ContourTexture.delete} it.
 *
 * Bounds are in the image's **raw** voxel scale (already converted from display
 * units — HU / SUVbw — upstream). Each mask voxel is classified by sampling the
 * image's un-transformed parent volume at that voxel's world-space center
 * (nearest-neighbour, see {@link sampleIntensityMaskSlice}). Sampling by world
 * position means the threshold applies correctly regardless of the stroke's
 * orientation or sub-voxel offset relative to the image, so it is consistent
 * across transversal / sagittal / coronal and anisotropic views, and the live preview
 * (same builder on the preview grid) matches the committed result.
 */
export function buildIntensityMask(
    stroke: ContourTexture,
    image: ImageObject,
    rawLower: number,
    rawUpper: number
): ContourTexture {
    const imageGrid = image.parent.grid;
    const floatPixelData = image.parent.floatPixelData;
    // Empty texture aligned to the stroke's grid (same base as buildConstraintMask).
    const mask = stroke.unionAligned(stroke);
    const maskGrid = mask.textureGrid;
    const sliceCount = maskGrid.sizeZ;

    const slices: Uint8Array[] = new Array(sliceCount);
    for (let z = 0; z < sliceCount; z++) {
        slices[z] = sampleIntensityMaskSlice(
            maskGrid,
            imageGrid,
            floatPixelData,
            z,
            rawLower,
            rawUpper
        );
    }
    mask.resetSdfData(slices);
    return mask;
}

/** Which constraints a call to {@link applyDrawingConstraintsToTexture} applied. */
export interface AppliedDrawingConstraints {
    boundingApplied: boolean;
    protectedApplied: boolean;
    intensityApplied: boolean;
}

export function anyConstraintApplied(
    applied: AppliedDrawingConstraints
): boolean {
    return (
        applied.boundingApplied ||
        applied.protectedApplied ||
        applied.intensityApplied
    );
}

/**
 * Clip a contour texture in place to
 * `((target ∩ bounding) ∩ intensityBand) \ ⋃ protected`, on the target's own
 * grid, freeing every constraint mask however the apply ends.
 *
 * @returns the constraints actually applied, not those configured.
 */
export function applyDrawingConstraintsToTexture(
    target: ContourTexture,
    image: ImageObject,
    constraints: DrawingConstraints | undefined,
    targetRoiId: RoiId
): AppliedDrawingConstraints {
    const masks = buildConstraintMasks(target, constraints, image, targetRoiId);
    if (masks) {
        try {
            applyConstraintMasks(target, masks);
        } finally {
            deleteConstraintMasks(masks);
        }
    }
    return {
        boundingApplied: Boolean(masks?.bounding),
        protectedApplied: Boolean(masks?.protected),
        intensityApplied: Boolean(masks?.intensity),
    };
}
